import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { onboard, getBetaInvite, IS_MOCK } from '../api'
import { setSession } from '../session'
import { usePublicSignup } from '../publicConfig'
import { focusAreaLimit, FOCUS_AREA_LIMITS } from '../tiers'
import { MOCK_CLIENT_ID, MOCK_ORG_NAME } from '../mockData'
import Button from '../components/Button'
import Input from '../components/Input'
import Spinner from '../components/Spinner'
import { ErrorAlert } from '../components/Alert'
import { FOCUS_AREA_OPTIONS, US_STATES } from '../options'
import { grantRangeError } from '../grantRange'
import MissionHints from '../components/MissionHints'
import AuthHeader from '../components/AuthHeader'
import HostLink from '../components/HostLink'
import { hrefFor } from '../twoHost'
import { useDocumentTitle } from '../components/PublicLayout'

const STEPS = ['Your Organization', 'Your Mission', 'Focus Areas', 'Confirmation']

/** Shown when signup is closed (no/invalid invite) — captures interest via mailto. */
function InviteOnlyNotice({ variant }) {
  const expired = variant === 'expired'
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <AuthHeader />
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
          <h2 className="text-xl font-semibold text-gray-900">
            {expired ? 'This invite link isn’t valid' : 'Praenum is in private beta'}
          </h2>
          <p className="text-sm text-gray-600 mt-3">
            {expired
              ? 'Your invite may have expired or already been used. If you think this is a mistake, get in touch and we’ll sort it out.'
              : 'We’re onboarding a small group of nonprofits by invitation right now. Public signup will open soon.'}
          </p>
          <div className="mt-6">
            <a href="mailto:hello@praenum.com">
              <Button variant="primary">Email hello@praenum.com</Button>
            </a>
          </div>
          <p className="text-xs text-gray-500 mt-6">
            Already have an account?{' '}
            <Link to="/signin" className="text-brand-500 hover:underline">Sign In</Link>
          </p>
        </div>
      </div>
    </div>
  )
}

export default function Onboard() {
  useDocumentTitle('Get started')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const inviteToken = searchParams.get('invite') || ''
  const publicSignup = usePublicSignup()

  const [step, setStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Beta-invite validation state: 'none' (no ?invite=), 'loading', 'valid',
  // 'invalid'. A valid invite unlocks a free, un-clocked Pro (beta) account.
  const [inviteState, setInviteState] = useState(inviteToken ? 'loading' : 'none')
  const [invite, setInvite] = useState(null) // { email, plan_tier }

  const [form, setForm] = useState({
    email: '',
    password: '',
    org_name: '',
    state: '',
    plan_tier: 'starter',
    typical_grant_min: '',
    typical_grant_max: '',
    mission: '',
    focus_areas: [],
  })

  // Validate an invite link on mount. On success, prefill + lock the email and
  // adopt the invite's tier (drives the focus-area cap + the confirm summary).
  useEffect(() => {
    if (!inviteToken) return
    let alive = true
    getBetaInvite(inviteToken)
      .then(data => {
        if (!alive) return
        setInvite(data)
        setInviteState('valid')
        setForm(f => ({ ...f, email: data.email, plan_tier: data.plan_tier }))
      })
      .catch(() => { if (alive) setInviteState('invalid') })
    return () => { alive = false }
  }, [inviteToken])

  const isBeta = inviteState === 'valid'

  // Focus-area cap follows the (beta) tier; Starter's 3 for a normal signup.
  const focusLimit = focusAreaLimit(form.plan_tier)

  function set(field, value) {
    setForm(f => ({ ...f, [field]: value }))
  }

  function toggleFocus(code) {
    setForm(f => ({
      ...f,
      focus_areas: f.focus_areas.includes(code)
        ? f.focus_areas.filter(c => c !== code)
        : f.focus_areas.length < focusLimit ? [...f.focus_areas, code] : f.focus_areas,
    }))
  }

  function validateStep() {
    if (step === 0) {
      if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
        return 'Please enter a valid email address.'
      if (!form.password || form.password.length < 8)
        return 'Please choose a password with at least 8 characters.'
      if (!form.org_name || form.org_name.length < 2)
        return 'Please enter your organization name.'
      if (!form.state)
        return 'Please select your state.'
      const rangeError = grantRangeError(form.typical_grant_min, form.typical_grant_max)
      if (rangeError) return rangeError
    }
    if (step === 1) {
      if (!form.mission || form.mission.length < 20)
        return 'Please write a mission statement (at least 20 characters).'
    }
    if (step === 2) {
      if (form.focus_areas.length === 0)
        return 'Please select at least one focus area.'
    }
    return null
  }

  function next() {
    const err = validateStep()
    if (err) { setError(err); return }
    setError('')
    setStep(s => s + 1)
  }

  function back() {
    setError('')
    setStep(s => s - 1)
  }

  // Jump straight to an earlier (already-visited) step — used by the clickable
  // progress indicator. We only allow going backward; advancing still requires
  // passing per-step validation via Continue. All field values live in one
  // `form` object that's never reset, so every prior field stays editable.
  function goToStep(target) {
    if (target < step) {
      setError('')
      setStep(target)
    }
  }

  // Everything the wizard holds lives in this component's state, so following
  // any link off the page discards it. Ask first once there is something to
  // lose. The email alone does not count, because an invite fills it in.
  const hasInput = step > 0 || Boolean(form.password || form.org_name || form.mission)
  function confirmLeave(e) {
    if (hasInput && !window.confirm('Leave signup? What you have entered so far will not be saved.')) {
      e.preventDefault()
    }
  }

  // Named once: the Terms notice quotes the button, so the two cannot drift.
  const submitLabel = isBeta ? 'Create my account' : 'Start Free Trial'

  async function submit() {
    setLoading(true)
    setError('')
    try {
      const payload = {
        email: form.email,
        password: form.password,
        org_name: form.org_name,
        state: form.state,
        mission: form.mission,
        focus_areas: form.focus_areas,
        plan_tier: form.plan_tier,
        typical_grant_min: Number(form.typical_grant_min),
        typical_grant_max: Number(form.typical_grant_max),
        // No org_ein: the signup form no longer collects one (see the note at
        // the EIN field's old position). POST /api/onboard still ACCEPTS the
        // field — the schema is deliberately left tolerant so this is not a
        // breaking API change — the wizard simply never sends it.
        // Beta redemption — the backend re-validates and ignores any
        // client-supplied tier in favor of the invite's.
        invite_token: isBeta ? inviteToken : undefined,
      }
      const data = await onboard(payload)
      // Onboard sets an HttpOnly auth cookie, so the new user is logged in
      // immediately. Cache the non-secret client summary so the post-
      // confirmation "Go to my dashboard" button works without a separate login.
      setSession({
        client: { client_id: data.client_id, org_name: form.org_name },
      })
      // Double opt-in: the account starts UNCONFIRMED and no digest is sent
      // until the email is confirmed. Send the user to confirm first rather
      // than straight to the dashboard.
      navigate('/check-inbox', { state: { email: form.email } })
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // ── Gate: which screen do we show? ──────────────────────────────────────────
  // An invite link is validating: show a spinner.
  if (inviteState === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spinner />
      </div>
    )
  }
  // An invite link that turned out invalid/expired → the notice card.
  if (inviteState === 'invalid') {
    return <InviteOnlyNotice variant="expired" />
  }
  // No invite + public signup closed (private beta) → the notice card.
  if (inviteState === 'none' && !publicSignup) {
    return <InviteOnlyNotice variant="closed" />
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">

        <AuthHeader subtitle="Weekly grant digest for nonprofits" onLeave={confirmLeave} />

        {/* Beta invite banner */}
        {isBeta && (
          <div className="mb-6 rounded-xl border border-brand-300 bg-brand-100 px-5 py-4 text-center">
            <p className="text-sm font-semibold text-brand-900">
              🎉 Beta invite: {form.plan_tier.charAt(0).toUpperCase() + form.plan_tier.slice(1)} plan, free
            </p>
            <p className="text-xs text-gray-600 mt-1">
              No credit card and no trial clock. Just set up your profile below.
            </p>
          </div>
        )}

        {/* Progress — earlier (completed) steps are clickable so any prior
            field (e.g. grant size on step 1) can be revisited and edited. */}
        <div className="flex items-center gap-2 mb-8">
          {STEPS.map((label, i) => {
            const isPast = i < step
            return (
              <React.Fragment key={i}>
                <div className="flex flex-col items-center gap-1">
                  <button
                    type="button"
                    onClick={() => goToStep(i)}
                    disabled={!isPast}
                    title={isPast ? `Back to ${label}` : undefined}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold
                      ${isPast ? 'bg-brand-500 text-white cursor-pointer hover:bg-brand-400' :
                        i === step ? 'bg-brand-900 text-white' :
                        'bg-gray-200 text-gray-500 cursor-default'}`}>
                    {isPast ? '✓' : i + 1}
                  </button>
                  <span className="text-xs text-gray-500 hidden sm:block">{label}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mb-5 ${i < step ? 'bg-brand-500' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            )
          })}
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <ErrorAlert message={error} />

          {/* Step 0 — Org info */}
          {step === 0 && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">Your Organization</h2>
              <p className="text-sm text-gray-500">We'll use this to find grants that fit.</p>
              <Input label="Work email" type="email" required value={form.email}
                onChange={e => set('email', e.target.value)}
                autoComplete="username"
                readOnly={isBeta}
                disabled={isBeta}
                hint={isBeta ? 'Your invite is tied to this address.' : undefined}
                placeholder="director@nonprofit.org" />
              <Input label="Password" type="password" required value={form.password}
                onChange={e => set('password', e.target.value)}
                autoComplete="new-password"
                placeholder="••••••••" hint="At least 8 characters." />
              <Input label="Organization name" required value={form.org_name}
                onChange={e => set('org_name', e.target.value)}
                placeholder="Green Valley Community Foundation" />
              <div className="flex flex-col gap-1">
                <label htmlFor="onboard-state" className="text-sm font-medium text-gray-700">
                  State <span className="text-red-500" aria-hidden="true">*</span>
                </label>
                <select
                  id="onboard-state"
                  required
                  value={form.state}
                  onChange={e => set('state', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
                    focus:outline-none focus:ring-2 focus:ring-brand-500">
                  <option value="">Select state…</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {/*
                REQUIRED since 2026-09-29 (#103). A blank range scores zero on
                grant-size fit for every foundation, which cost a client about
                half its leads and told it nothing. No numeric placeholder, on
                purpose: an example that could be a valid answer gets submitted
                as the answer (the 2026-08-13 EIN finding).
              */}
              <div className="grid grid-cols-2 gap-3">
                <Input label="Typical grant min ($)" type="number" min="0" required
                  value={form.typical_grant_min}
                  onChange={e => set('typical_grant_min', e.target.value)}
                  hint="The smallest grant you usually apply for." />
                <Input label="Typical grant max ($)" type="number" min="1" required
                  value={form.typical_grant_max}
                  onChange={e => set('typical_grant_max', e.target.value)}
                  hint="The largest. We rank foundations by what they actually give." />
              </div>
              {/*
                ⚠️ THERE IS NO EIN FIELD HERE ANY MORE, on purpose (2026-08-13).

                It was optional, and it produced approximately zero real EINs —
                #62 measured that, and the ONE client EIN that ever reached prod
                was `04-2121342`, verbatim the placeholder this field used to
                show. A free-text field with a realistic-looking example does not
                collect data, it teaches people to copy the example.

                It is also not in the minimum viable profile (email, org name,
                state, mission, focus areas) that "under 5 minutes" is measured
                against, and it is not a matching input, so nothing is lost here.

                The EIN is now OFFERED rather than asked for, after email
                confirmation, by `OrgIdentitySetup` on the dashboard — which can
                reach `GET /api/org-lookup` because that route requires a
                confirmed account (ADR-0018) and a signup form by definition is
                not one. Preferences keeps a typed field as the deliberate
                fallback. See #65.
              */}
            </div>
          )}

          {/* Step 1 — Mission (the single highest-value input) */}
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">Your Mission</h2>
              <p className="text-sm text-gray-600">
                This is the most important thing you'll write here. We match you to the
                private foundations and grants that fund work like yours by reading this
                statement, so the more specific it is, the better your matches.
              </p>

              {/* WHO / WHAT / WHERE scaffold */}
              <div className="rounded-xl bg-brand-100 p-4 text-sm">
                <p className="font-medium text-brand-900 mb-2">Write 3–5 sentences that cover:</p>
                <ul className="space-y-1.5 text-gray-700">
                  <li><strong className="text-brand-900">Who</strong> you serve: the specific
                    people or communities (e.g. “low-income middle-schoolers,” “rural veterans”).</li>
                  <li><strong className="text-brand-900">What</strong> you actually do: your
                    programs and activities, not just your values.</li>
                  <li><strong className="text-brand-900">Where</strong> you work: city, county,
                    region, or state.</li>
                </ul>
              </div>

              <Input
                as="textarea"
                label="Mission statement"
                required
                rows={6}
                value={form.mission}
                onChange={e => set('mission', e.target.value)}
                placeholder="Example: We help low-income middle- and high-school students in rural Halifax County, North Carolina graduate ready for college and careers. We run free after-school tutoring, a summer STEM camp, and one-on-one college-application mentoring. Most of the families we serve are first-generation college households, and we partner with three local school districts to reach them."
              />
              <MissionHints mission={form.mission} state={form.state} />
            </div>
          )}

          {/* Step 2 — Focus areas */}
          {step === 2 && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">Focus Areas</h2>
              <p className="text-sm text-gray-500">
                Select the categories that best describe your work. We'll use these to filter
                grants before matching. Choose up to {focusLimit === Infinity ? 'any' : focusLimit}.
              </p>
              <div className="grid grid-cols-2 gap-2">
                {FOCUS_AREA_OPTIONS.map(({ code, label }) => {
                  const selected = form.focus_areas.includes(code)
                  return (
                    <button
                      key={code}
                      type="button"
                      onClick={() => toggleFocus(code)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm
                        text-left transition-colors
                        ${selected
                          ? 'border-brand-500 bg-brand-100 text-brand-900 font-medium'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-brand-300'
                        }`}
                    >
                      <span className={`w-4 h-4 rounded flex-shrink-0 border-2 flex items-center justify-center
                        ${selected ? 'border-brand-500 bg-brand-500' : 'border-gray-300'}`}>
                        {selected && <span className="text-white text-xs">✓</span>}
                      </span>
                      {label}
                    </button>
                  )
                })}
              </div>
              <p className="text-xs text-gray-500">
                {form.focus_areas.length}{focusLimit === Infinity ? '' : `/${focusLimit}`} selected
              </p>
              {!isBeta && form.plan_tier === 'starter' && (
                <p className="text-xs text-gray-500">
                  Starter includes up to {FOCUS_AREA_LIMITS.starter} focus areas.{' '}
                  <HostLink to="/pricing" onClick={confirmLeave} className="text-brand-500 hover:underline">
                    Growth includes up to {FOCUS_AREA_LIMITS.growth} →
                  </HostLink>
                </p>
              )}
            </div>
          )}

          {/* Step 3 — Confirm */}
          {step === 3 && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">Ready to go!</h2>
              <p className="text-sm text-gray-500">
                {isBeta
                  ? <>Review your setup and click <strong>{submitLabel}</strong>. Your first digest will arrive next Tuesday morning.</>
                  : <>Review your setup and click <strong>{submitLabel}</strong> to begin your 14-day free trial. Your first digest will arrive next Tuesday morning.</>}
              </p>
              <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Email</span>
                  <span className="font-medium">{form.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Organization</span>
                  <span className="font-medium">{form.org_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">State</span>
                  <span className="font-medium">{form.state}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Focus areas</span>
                  <span className="font-medium text-right">
                    {form.focus_areas.join(', ')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Plan</span>
                  <span className="font-medium capitalize">
                    {isBeta
                      ? `${form.plan_tier} (beta, free)`
                      : `${form.plan_tier} (14-day free trial)`}
                  </span>
                </div>
                {(form.typical_grant_min || form.typical_grant_max) && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Typical grant size</span>
                    <span className="font-medium">
                      {form.typical_grant_min ? `$${Number(form.typical_grant_min).toLocaleString()}` : 'Not set'}
                      {' – '}
                      {form.typical_grant_max ? `$${Number(form.typical_grant_max).toLocaleString()}` : 'Not set'}
                    </span>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500">
                Need to change something? Click any completed step above to edit it.
              </p>
            </div>
          )}

          {/*
            The Terms say "By creating an account ... you agree to these
            Terms", and until 2026-10-16 signup never showed them (#206). This
            is the notice that makes that sentence true. What courts look for
            in a "sign-in-wrap" (Berman v. Freedom Financial, 9th Cir. 2022;
            Kauders v. Uber, Mass. 2021), and so what not to undo here:
              * directly beside the button, and read before it, not after;
              * names the exact button the visitor clicks (submitLabel);
              * body-size text at full contrast, never small grey print;
              * links that look like links: coloured AND underlined.
            The links open a new tab because the wizard's answers live in
            component state, and leaving this page would discard them.
            Whether a checkbox should replace this is a question for counsel
            (#24).
          */}
          {step === STEPS.length - 1 && (
            <p className="mt-6 text-sm text-gray-700">
              By clicking <strong>{submitLabel}</strong>, you agree to the{' '}
              <a href={hrefFor('/terms')} target="_blank" rel="noopener noreferrer"
                className="text-brand-600 underline hover:text-brand-500">
                Terms of Service<span className="sr-only"> (opens in a new tab)</span>
              </a>{' '}
              and acknowledge the{' '}
              <a href={hrefFor('/privacy')} target="_blank" rel="noopener noreferrer"
                className="text-brand-600 underline hover:text-brand-500">
                Privacy Policy<span className="sr-only"> (opens in a new tab)</span>
              </a>.
            </p>
          )}

          {/* Navigation */}
          <div className={`flex justify-between ${step === STEPS.length - 1 ? 'mt-4' : 'mt-6'}`}>
            {step > 0
              ? <Button variant="secondary" onClick={back}>← Back</Button>
              : <div />
            }
            {step < STEPS.length - 1
              ? <Button onClick={next}>Continue →</Button>
              : <Button onClick={submit} loading={loading}>
                  {submitLabel}
                </Button>
            }
          </div>
        </div>

        <p className="text-center text-xs text-gray-500 mt-6">
          Already have an account?{' '}
          <Link to="/signin" onClick={confirmLeave} className="text-brand-500 hover:underline">Sign In</Link>
        </p>

        {/* Dev-only shortcut — only visible when VITE_MOCK=true */}
        {IS_MOCK && (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-center">
            <p className="text-xs text-yellow-700 font-medium mb-2">
              🛠 Dev mode: skip signup and log in with mock data
            </p>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setSession({
                  client: { client_id: MOCK_CLIENT_ID, org_name: MOCK_ORG_NAME },
                })
                navigate('/dashboard')
              }}
            >
              Enter as Demo User →
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
